import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { GraduationCap, Mail, Phone, BookOpen, UserPlus, Copy, Check } from 'lucide-react';

/**
 * Trainers tab — list formateurs, create new, assign to formations.
 * Uses POST /api/admin/trainers and PUT /api/admin/trainers/{id}/assignments.
 */
export const TrainersAdmin = () => {
  const [trainers, setTrainers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [assignTrainer, setAssignTrainer] = useState(null);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  const emptyForm = { first_name: '', last_name: '', email: '', phone: '', bio: '', specialties: '' };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const [allUsers, allCourses] = await Promise.all([
        api.getAllUsers(),
        api.getCourses(),
      ]);
      setTrainers(allUsers.filter((u) => u.role === 'formateur'));
      setCourses(allCourses);
    } catch (e) {
      toast.error('Erreur lors du chargement des formateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error('Prénom, nom et email sont obligatoires');
      return;
    }
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        bio: form.bio.trim() || null,
        specialties: form.specialties
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const res = await api.createTrainer(payload);
      toast.success('Formateur créé');
      setCreatedInfo({ email: res.email, temp_password: res.temp_password });
      setForm(emptyForm);
      setCreateOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const copyCreds = async () => {
    if (!createdInfo) return;
    try {
      await navigator.clipboard.writeText(
        `Email : ${createdInfo.email}\nMot de passe temporaire : ${createdInfo.temp_password}`
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Impossible de copier');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-[#4A5568]">Chargement…</div>;
  }

  return (
    <div className="space-y-6" data-testid="trainers-admin">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">FORMATEURS</p>
          <h2 className="font-light text-3xl md:text-4xl text-[#1F2A4A] tracking-tight">
            L&apos;équipe <span className="font-serif italic text-[#C9B896]">pédagogique.</span>
          </h2>
          <p className="text-sm text-[#4A5568] mt-2">
            Crée des comptes formateurs et affecte-leur les formations qu&apos;ils animent.
          </p>
        </div>
        <Button
          onClick={() => { setForm(emptyForm); setCreateOpen(true); }}
          className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full px-6"
          data-testid="create-trainer-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Créer un formateur
        </Button>
      </div>

      {/* Trainers grid */}
      {trainers.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#1F2A4A]/10 rounded-2xl">
          <GraduationCap className="w-12 h-12 text-[#C9B896] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#1F2A4A] mb-2">Aucun formateur</h3>
          <p className="text-sm text-[#4A5568]">Crée un premier formateur pour le voir ici.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {trainers.map((t) => {
            const assigned = (t.assigned_course_ids || [])
              .map((id) => courses.find((c) => c.id === id))
              .filter(Boolean);
            return (
              <div
                key={t.id}
                className="bg-white border border-[#1F2A4A]/10 rounded-2xl p-6 hover:border-[#C9B896]/60 transition-colors"
                data-testid={`trainer-card-${t.id}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#1F2A4A] text-[#C9B896] flex items-center justify-center font-serif text-lg flex-shrink-0">
                    {(t.first_name || t.email).slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-xl text-[#1F2A4A] font-light leading-tight truncate">
                      {t.first_name} {t.last_name}
                    </p>
                    <p className="text-xs text-[#4A5568] flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3" /> {t.email}</p>
                    {t.phone && <p className="text-xs text-[#4A5568] flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" /> {t.phone}</p>}
                  </div>
                </div>

                {t.bio && <p className="text-xs text-[#4A5568] italic mb-3 leading-relaxed">{t.bio}</p>}

                {t.specialties?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {t.specialties.map((s) => (
                      <Badge key={s} variant="outline" className="text-[10px] border-[#C9B896]/40 text-[#1F2A4A]">{s}</Badge>
                    ))}
                  </div>
                )}

                <div className="pt-3 border-t border-[#1F2A4A]/8">
                  <p className="text-[10px] uppercase tracking-wider text-[#C9B896] font-semibold mb-2 flex items-center gap-1.5">
                    <BookOpen className="w-3 h-3" /> Formations affectées ({assigned.length})
                  </p>
                  {assigned.length === 0 ? (
                    <p className="text-xs text-[#4A5568] italic">Aucune formation affectée.</p>
                  ) : (
                    <ul className="space-y-1">
                      {assigned.map((c) => (
                        <li key={c.id} className="text-xs text-[#1F2A4A]">• {c.title}</li>
                      ))}
                    </ul>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 w-full border-[#1F2A4A]/15 text-[#1F2A4A] hover:bg-[#FAF7F0]"
                    onClick={() => setAssignTrainer(t)}
                    data-testid={`trainer-assign-${t.id}`}
                  >
                    Gérer les affectations
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1F2A4A] font-light">Créer un formateur</DialogTitle>
            <DialogDescription>
              Un mot de passe temporaire sera généré. Le formateur pourra le modifier après connexion.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Prénom *</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="trainer-firstname" />
              </div>
              <div>
                <Label className="text-xs">Nom *</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="trainer-lastname" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="trainer-email" />
            </div>
            <div>
              <Label className="text-xs">Téléphone</Label>
              <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="trainer-phone" />
            </div>
            <div>
              <Label className="text-xs">Spécialités (séparées par des virgules)</Label>
              <Input
                value={form.specialties}
                onChange={(e) => setForm({ ...form, specialties: e.target.value })}
                placeholder="Sellerie auto, Cuir, Couture industrielle"
                data-testid="trainer-specialties"
              />
            </div>
            <div>
              <Label className="text-xs">Bio courte</Label>
              <Textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                className="min-h-[80px]"
                placeholder="Parcours, années d&apos;expérience, atelier d&apos;origine…"
                data-testid="trainer-bio"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA]" data-testid="trainer-submit">
                Créer
              </Button>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Created credentials dialog */}
      <Dialog open={!!createdInfo} onOpenChange={(open) => !open && setCreatedInfo(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#1F2A4A] font-light">Formateur créé ✓</DialogTitle>
            <DialogDescription>
              Transmets ces identifiants au formateur. Ils n&apos;apparaîtront qu&apos;une seule fois.
            </DialogDescription>
          </DialogHeader>
          {createdInfo && (
            <div className="space-y-3 bg-[#FAF7F0] border border-[#C9B896]/30 rounded-xl p-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#C9B896] mb-1">Email</p>
                <p className="font-mono text-sm text-[#1F2A4A]">{createdInfo.email}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#C9B896] mb-1">Mot de passe temporaire</p>
                <p className="font-mono text-sm text-[#1F2A4A]">{createdInfo.temp_password}</p>
              </div>
              <Button onClick={copyCreds} className="w-full bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166]" data-testid="copy-trainer-creds">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copié' : 'Copier les identifiants'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assignment dialog */}
      {assignTrainer && (
        <AssignFormationsDialog
          trainer={assignTrainer}
          courses={courses}
          onClose={() => setAssignTrainer(null)}
          onSaved={() => { setAssignTrainer(null); load(); }}
        />
      )}
    </div>
  );
};

const AssignFormationsDialog = ({ trainer, courses, onClose, onSaved }) => {
  const [selected, setSelected] = useState(new Set(trainer.assigned_course_ids || []));
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.updateTrainerAssignments(trainer.id, Array.from(selected));
      toast.success('Affectations enregistrées');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-[#1F2A4A] font-light">
            Affecter {trainer.first_name} aux formations
          </DialogTitle>
          <DialogDescription>
            Coche les formations que ce formateur animera. Les sessions héritent automatiquement de l&apos;affectation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          {courses.map((c) => (
            <label
              key={c.id}
              className="flex items-start gap-3 p-3 rounded-lg border border-[#1F2A4A]/10 hover:bg-[#FAF7F0] cursor-pointer"
              data-testid={`assign-course-${c.id}`}
            >
              <Checkbox
                checked={selected.has(c.id)}
                onCheckedChange={() => toggle(c.id)}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1F2A4A] truncate">{c.title}</p>
                <p className="text-xs text-[#4A5568]">{c.course_type === 'online' ? 'En ligne' : 'Sur place'} · {c.price?.toFixed(0)} €</p>
              </div>
            </label>
          ))}
        </div>
        <div className="flex gap-2 pt-3 border-t border-[#1F2A4A]/10 mt-3">
          <Button onClick={save} disabled={saving} className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA]" data-testid="assign-save">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrainersAdmin;
